import { TrackingEvent } from '@acretrader/acremaps-analytics-events';
import { faEnvelope } from '@fortawesome/sharp-solid-svg-icons';
import { usePathname, useRouter } from 'next/navigation';
import { FC, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { AUTH_ACTIONS } from '@/constants/constants';
import {
  LATEST_CREATED_LEAD_LIST_DETAILS,
  LATEST_CREATED_LEAD_LIST_PREVIEW,
} from '@/constants/localStorage';
import { LEAD_LIST_RESULTS_URL } from '@/constants/urls';
import {
  createLeadList,
  LeadList,
  LeadListPreview,
} from '@/features/search/leadLists/helpers/createLeadList';
import { ExportMenuButton } from '@/features/selectionsPanel/exportMenu/exportMenuLink';
import { useEnhancedSearchParams } from '@/hooks/useEnhancedSearchParams';
import { useAnalytics } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/auth-provider';
import { useToastContext } from '@/providers/ToastProvider';
import type { RootState } from '@/redux/store';
import { bindLoadingStateToCallback } from '@/utils/bindLoadingStateToCallback';
import { multiUnion } from '@/utils/multiUnion';
import { saveJsonInLocalStorage } from '@/utils/webStorage';

export const CreateLeadListOption: FC = () => {
  const router = useRouter();
  const pathName = usePathname();
  const { showErrorToast } = useToastContext();
  const { userCanPurchaseLeadLists } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const { track } = useAnalytics();

  const allSelectionFeatures = useSelector(
    (state: RootState) => state.features.allFeatures
  );

  const { setOrReplaceParamWithRerender } = useEnhancedSearchParams();

  const storeLeadListInfoForFutureDisplay = useCallback(
    (leadList: LeadList, leadListPreview: LeadListPreview) => {
      saveJsonInLocalStorage(LATEST_CREATED_LEAD_LIST_DETAILS, leadList);
      saveJsonInLocalStorage(LATEST_CREATED_LEAD_LIST_PREVIEW, leadListPreview);
    },
    []
  );

  const createList = useCallback(async () => {
    const geometry = multiUnion(allSelectionFeatures)?.geometry;
    const { res, err } = await createLeadList({ filter: { geometry } });
    if (err) showErrorToast('Error creating lead list.');
    if (res?.data) {
      track({
        data: {},
        event: TrackingEvent.LEAD_LIST_MADE_FROM_SELECTION,
      });
      storeLeadListInfoForFutureDisplay({ ...res.data }, res.data.preview);
      if (pathName === LEAD_LIST_RESULTS_URL) {
        setOrReplaceParamWithRerender(
          'shouldRerenderLeadListResultsPage',
          'true'
        );
      } else {
        router.push(LEAD_LIST_RESULTS_URL);
      }
    }
  }, [
    allSelectionFeatures,
    pathName,
    router,
    setOrReplaceParamWithRerender,
    showErrorToast,
    storeLeadListInfoForFutureDisplay,
    track,
  ]);

  const handleClick = useCallback(async () => {
    await bindLoadingStateToCallback(createList, setIsLoading);
  }, [createList]);

  const tooltip: string = useMemo(() => {
    if (!userCanPurchaseLeadLists) return AUTH_ACTIONS.UPGRADE_TO_ACCESS;
    if (!allSelectionFeatures?.length) return 'Make a Selection';
    return '';
  }, [allSelectionFeatures, userCanPurchaseLeadLists]);

  return (
    <ExportMenuButton
      disabled={!userCanPurchaseLeadLists || !allSelectionFeatures?.length}
      icon={faEnvelope}
      iconClassName="text-gray-70"
      isLoading={isLoading}
      label="Lead List"
      tooltip={tooltip}
      onClick={handleClick}
    />
  );
};
